import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Model, type SurveyModel } from 'survey-core';
import { Survey } from 'survey-react-ui';
import 'survey-core/survey-core.css';
import { ApiError, api } from '../api';

type RenderResponse = {
  survey_key: string;
  schema: Record<string, unknown>;
  prefill: { first_name: string | null; dock_name: string | null };
};

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; data: RenderResponse }
  | { kind: 'gone'; reason: 'not_found' | 'expired' | 'closed' | 'already_completed' }
  | { kind: 'error' };

export function SurveyPage(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  useEffect(() => {
    if (!token) {
      setState({ kind: 'gone', reason: 'not_found' });
      return;
    }
    let cancelled = false;
    api<RenderResponse>(`/api/surveys/render/${encodeURIComponent(token)}`)
      .then((data) => { if (!cancelled) setState({ kind: 'ready', data }); })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError) {
          if (err.status === 404) { setState({ kind: 'gone', reason: 'not_found' }); return; }
          if (err.status === 410) {
            const body = err.body as { error?: string } | null;
            const reason = (body?.error ?? 'expired') as 'expired' | 'closed' | 'already_completed';
            setState({ kind: 'gone', reason });
            return;
          }
        }
        setState({ kind: 'error' });
      });
    return () => { cancelled = true; };
  }, [token]);

  const model: SurveyModel | null = useMemo(() => {
    if (state.kind !== 'ready') return null;
    const m = new Model(state.data.schema);
    // Pipe member context into title placeholders ({{first_name}}, {{dock_name}}).
    m.setVariable('first_name', state.data.prefill.first_name ?? 'there');
    m.setVariable('dock_name', state.data.prefill.dock_name ?? 'the dock');
    return m;
  }, [state]);

  useEffect(() => {
    if (!model || !token) return;

    const onPageChange = (sender: SurveyModel): void => {
      void api(`/api/responses/${encodeURIComponent(token)}/partial`, {
        method: 'POST',
        body: JSON.stringify({ data: sender.data }),
      }).catch(() => { /* network blip — let onComplete retry */ });
    };

    const onComplete = (sender: SurveyModel): void => {
      void api(`/api/responses/${encodeURIComponent(token)}/complete`, {
        method: 'POST',
        body: JSON.stringify({ data: sender.data }),
      }).catch(() => { /* completedHtml still shows; handler will see it didn't land in audit */ });
    };

    model.onCurrentPageChanged.add(onPageChange);
    model.onComplete.add(onComplete);

    return () => {
      model.onCurrentPageChanged.remove(onPageChange);
      model.onComplete.remove(onComplete);
    };
  }, [model, token]);

  const SurveyHeader = (
    <div className="survey-header">
      <div className="logo-circle">
        <img src="/fbc-logo.png" alt="Freedom Boat Club" />
      </div>
      <div className="brand">Freedom Boat Club</div>
    </div>
  );

  if (state.kind === 'loading') return <div className="survey-shell">{SurveyHeader}<p className="muted" style={{textAlign:'center',marginTop:'2rem'}}>Loading…</p></div>;

  if (state.kind === 'gone') {
    const headline =
      state.reason === 'already_completed' ? 'Already submitted' :
      state.reason === 'expired'           ? 'Link expired' :
      state.reason === 'closed'            ? 'Survey closed' :
      'Link not found';
    const body =
      state.reason === 'already_completed' ? 'Thanks — we received your earlier response. See you on the water!' :
      state.reason === 'expired'           ? 'This survey link has expired. If you still want to share feedback, email feedback@freedomboatclub.ai.' :
      state.reason === 'closed'            ? 'This survey is no longer accepting responses.' :
      'Double-check the link, or email feedback@freedomboatclub.ai if you need help.';
    return (
      <div className="survey-shell">
        {SurveyHeader}
        <div className="survey-card">
          <h1>{headline}</h1>
          <p className="muted">{body}</p>
        </div>
      </div>
    );
  }

  if (state.kind === 'error' || !model) {
    return (
      <div className="survey-shell">
        {SurveyHeader}
        <div className="survey-card">
          <h1>Something went wrong</h1>
          <p className="muted">Try again in a moment, or email feedback@freedomboatclub.ai.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="survey-shell">
      {SurveyHeader}
      <Survey model={model} />
    </div>
  );
}
